import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ServiceCard from "@/components/ServiceCard";
import ArticleCard from "@/components/ArticleCard";
import FAQAccordion from "@/components/FAQAccordion";
import CTABand from "@/components/CTABand";
import { DISCLAIMER } from "@/lib/constants";
import api from "@/lib/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSvc(null);
    setNotFound(false);
    api.get(`/public/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-navy">Service not found</h1>
        <Link to="/services" className="btn-gold mt-6">Back to Services</Link>
      </div>
    );
  }
  if (!svc) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  const area = svc.category === "business"
    ? { label: "Business Immigration", to: "/business" }
    : { label: "Individuals & Families", to: "/individuals" };
  const PARENTS = {
    "spouse-partner": "family-partner", "fiance-visa": "family-partner", "parent-route": "family-partner",
    "child-application": "family-partner", "adult-dependant-relative": "family-partner",
    "skilled-worker": "work-uk", "health-care-worker": "work-uk",
  };
  const PARENT_LABELS = { "family-partner": "Family & Partner", "work-uk": "Work in the UK" };
  const crumbs = [{ label: "Home", to: "/" }, area];
  if (PARENTS[svc.slug]) crumbs.push({ label: PARENT_LABELS[PARENTS[svc.slug]], to: `/services/${PARENTS[svc.slug]}` });
  crumbs.push({ label: svc.title });

  return (
    <>
      <Seo title={svc.seo_title || svc.title} description={svc.seo_desc || svc.short_desc} />
      <PageHero
        crumbs={crumbs}
        eyebrow={svc.category}
        title={svc.title}
        image={svc.image}
        subtitle={svc.hero_intro}>
        <Link to="/book-consultation" className="btn-gold">Book a Consultation <ArrowRight size={16} /></Link>
      </PageHero>

      <section className="bg-white py-16">
        <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* Sub-services */}
            {svc.sub_service_items?.length > 0 && (
              <div className="mb-12">
                <h2 className="mb-6 font-serif text-2xl font-semibold text-navy">Explore this area</h2>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {svc.sub_service_items.map((s) => <ServiceCard key={s.id} service={s} showImage={false} />)}
                </div>
              </div>
            )}

            {/* Who for */}
            {svc.who_for && (
              <div className="mb-10">
                <h2 className="mb-3 font-serif text-2xl font-semibold text-navy">Who this may suit</h2>
                <p className="text-base leading-relaxed text-slate-600">{svc.who_for}</p>
              </div>
            )}

            {/* Considerations */}
            {svc.considerations?.length > 0 && (
              <div className="mb-10">
                <h2 className="mb-4 font-serif text-2xl font-semibold text-navy">What may be considered</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {svc.considerations.map((c, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-cream p-5">
                      <div className="font-semibold text-navy">{c.label}</div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{c.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How we help */}
            {svc.how_we_help?.length > 0 && (
              <div className="mb-10 rounded-xl p-8" style={{ backgroundColor: "#0B192C" }}>
                <h2 className="mb-5 font-serif text-2xl font-semibold text-white">How ApexPath Can Help</h2>
                <ul className="space-y-3">
                  {svc.how_we_help.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-white">
                      <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-gold" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FAQs */}
            {svc.faqs?.length > 0 && (
              <div className="mb-10">
                <h2 className="mb-4 font-serif text-2xl font-semibold text-navy">Frequently Asked Questions</h2>
                <FAQAccordion faqs={svc.faqs} />
              </div>
            )}

            <p className="rounded-lg border-l-4 border-gold bg-cream p-4 text-xs italic text-slate-500">
              {DISCLAIMER}
            </p>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-xl border border-slate-200 bg-cream p-6">
              <h3 className="font-serif text-xl font-semibold text-navy">Need advice?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Your circumstances are individual. Speak to ApexPath about the requirements that apply to you.
              </p>
              <Link to="/book-consultation" data-testid="service-sidebar-book-btn" className="btn-gold mt-4 w-full text-sm">
                Book a Consultation <ArrowRight size={15} />
              </Link>
              <button onClick={() => navigate("/services")} className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm font-medium text-navy hover:text-gold">
                <ArrowLeft size={15} /> All services
              </button>
            </div>
          </aside>
        </div>
      </section>

      {/* Related articles */}
      {svc.related_article_items?.length > 0 && (
        <section className="bg-cream py-16">
          <div className="container-x">
            <h2 className="mb-8 font-serif text-2xl font-semibold text-navy">Related Insights</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {svc.related_article_items.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </>
  );
}
